import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Check, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { services } from '../mock';

export const Services = () => {
  const [activeIndex, setActiveIndex] = useState(0);

  const nextService = () => {
    setActiveIndex((prev) => (prev + 1) % services.length);
  };

  const prevService = () => {
    setActiveIndex((prev) => (prev - 1 + services.length) % services.length);
  };

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const activeService = services[activeIndex];

  // Service background images mapping
  const serviceImages = {
    1: 'https://images.pexels.com/photos/2138126/pexels-photo-2138126.jpeg',
    2: 'https://images.unsplash.com/photo-1479839672679-a46483c0e7c8?crop=entropy&cs=srgb&fm=jpg&q=85',
    3: 'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?crop=entropy&cs=srgb&fm=jpg&q=85',
    4: 'https://images.unsplash.com/photo-1694521787162-5373b598945c?crop=entropy&cs=srgb&fm=jpg&q=85'
  };

  return (
    <section id="services" className="relative h-[80vh] flex items-center overflow-hidden bg-black">
      {/* Full-screen Background Image with Parallax */}
      <div className="absolute inset-0">
        {services.map((service, index) => (
          <div
            key={service.id}
            className={`absolute inset-0 transition-opacity duration-1000 ${
              index === activeIndex ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <img
              src={serviceImages[service.id]}
              alt={service.title}
              className="w-full h-full object-cover"
            />
            {/* Dark overlay gradient */}
            <div className="absolute inset-0 bg-gradient-to-r from-black/95 via-black/70 to-black/40"></div>
          </div>
        ))}
      </div>

      {/* Content Container */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            
            {/* Left Side - Service Content */}
            <div className="space-y-8">
              {/* Section Badge */}
              <div className="inline-flex items-center space-x-2 bg-[#00CCDD] text-black px-6 py-2 rounded-full">
                <span className="font-bold text-sm uppercase tracking-wide">Unsere Leistungen</span>
              </div>

              {/* Service Number */}
              <div className="flex items-baseline space-x-4">
                <span className="text-[#00CCDD] text-7xl sm:text-8xl font-bold leading-none">
                  0{activeIndex + 1}
                </span>
                <span className="text-white/40 text-3xl font-light">/ 0{services.length}</span>
              </div>

              {/* Service Title with Slide Animation */}
              <div className="space-y-4">
                <h2 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white leading-tight animate-in fade-in slide-in-from-bottom-4 duration-700">
                  {activeService.title}
                </h2>
                <div className="w-24 h-1.5 bg-gradient-to-r from-[#00CCDD] to-transparent"></div>
              </div>

              {/* Service Description */}
              <p className="text-xl text-white/80 leading-relaxed animate-in fade-in duration-700">
                {activeService.description}
              </p>

              {/* Features List */}
              <ul className="space-y-4">
                {activeService.features.map((feature, idx) => (
                  <li 
                    key={`feature-${activeService.id}-${idx}`} 
                    className="flex items-center space-x-3 text-white/90 group animate-in fade-in duration-700"
                    style={{ animationDelay: `${idx * 100}ms` }}
                  >
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#00CCDD] flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Check className="w-5 h-5 text-black font-bold" />
                    </div>
                    <span className="text-lg group-hover:text-[#00CCDD] transition-colors">{feature}</span>
                  </li>
                ))}
              </ul>

              {/* CTA Button */}
              <Button
                onClick={scrollToContact}
                size="lg"
                className="bg-[#00CCDD] hover:bg-white text-black px-8 py-7 text-lg font-bold shadow-2xl hover:shadow-[#00CCDD]/50 transition-all duration-300 group"
              >
                Jetzt anfragen
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>

            {/* Right Side - Service Navigation Cards */}
            <div className="hidden lg:block space-y-4">
              {services.map((service, index) => (
                <button
                  key={service.id}
                  onClick={() => setActiveIndex(index)}
                  className={`w-full text-left p-6 rounded-2xl transition-all duration-500 group ${
                    index === activeIndex
                      ? 'bg-white/10 backdrop-blur-md border-2 border-[#00CCDD] scale-105'
                      : 'bg-white/5 backdrop-blur-sm border-2 border-white/10 hover:bg-white/10 hover:border-white/30'
                  }`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className={`text-4xl font-bold ${
                      index === activeIndex ? 'text-[#00CCDD]' : 'text-white/30'
                    }`}>
                      0{index + 1}
                    </span>
                    {index === activeIndex && (
                      <div className="w-3 h-3 rounded-full bg-[#00CCDD] animate-pulse"></div>
                    )}
                  </div>
                  <h3 className={`text-2xl font-bold mb-2 transition-colors ${
                    index === activeIndex ? 'text-white' : 'text-white/60 group-hover:text-white'
                  }`}>
                    {service.title}
                  </h3>
                  {index === activeIndex && (
                    <p className="text-white/70 text-sm animate-in fade-in duration-500">
                      {service.description}
                    </p>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Arrows */}
      <div className="absolute bottom-12 left-1/2 transform -translate-x-1/2 flex items-center space-x-6 z-20">
        <button
          onClick={prevService}
          className="w-14 h-14 rounded-full bg-white/10 backdrop-blur-md border-2 border-white/30 flex items-center justify-center hover:bg-[#00CCDD] hover:border-[#00CCDD] transition-all duration-300 group"
          aria-label="Vorheriger Service"
        >
          <ChevronLeft className="w-6 h-6 text-white group-hover:text-black" />
        </button>

        {/* Dot Indicators */}
        <div className="flex space-x-3">
          {services.map((service, index) => (
            <button
              key={`service-dot-${service.id}`}
              onClick={() => setActiveIndex(index)}
              className={`transition-all duration-300 rounded-full ${
                index === activeIndex
                  ? 'w-12 h-3 bg-[#00CCDD]'
                  : 'w-3 h-3 bg-white/30 hover:bg-white/50'
              }`}
              aria-label={`Service ${index + 1}`}
            />
          ))}
        </div>

        <button
          onClick={nextService}
          className="w-14 h-14 rounded-full bg-white/10 backdrop-blur-md border-2 border-white/30 flex items-center justify-center hover:bg-[#00CCDD] hover:border-[#00CCDD] transition-all duration-300 group"
          aria-label="Nächster Service"
        >
          <ChevronRight className="w-6 h-6 text-white group-hover:text-black" />
        </button>
      </div>

      {/* Scroll Indicator (Mobile Only) */}
      <div className="lg:hidden absolute bottom-8 left-1/2 transform -translate-x-1/2 text-white/60 text-sm animate-bounce">
        <span className="block text-center mb-2">Scrollen für mehr</span>
        <div className="w-6 h-10 border-2 border-white/40 rounded-full mx-auto flex items-start justify-center p-2">
          <div className="w-1.5 h-1.5 bg-white/60 rounded-full"></div>
        </div>
      </div>
    </section>
  );
};